import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import ProfileImageUploader from "../../components/Form/ProfileImageUploader";
import Button from "../../components/Form/CustomButton";
import CustomInput from "../../components/Form/Input";
import showToast from "../../utils/toastNotifications";
import { RiPencilFill } from "react-icons/ri";
import { useThunk } from "../../hooks/useThunk";
import { updateUserProfile } from "../../store";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import { firebaseUplaodFolders } from "../../constants/commonConstants";

const UserProfile = () => {
  // NOTE: useThunk is a custom hook that dispatches thunks
  const [doUpdateProfile, isUpdateProfile, updateProfileError] =
    useThunk(updateUserProfile);

  const [
    doUploadRefrencesToFirebase,
    isUploadRefrencesToFirebaseLoading,
    uploadRefrencesToFirebaseError,
  ] = useFirebaseFileUpload();

  // NOTE: useSelector is a hook from react-redux that extracts data from the Redux store state
  const { user } = useSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);

  const handleEditing = () => setIsEditing(!isEditing);

  // NOTE: useForm is a custom hook that handles form state and validation
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || "",
      jobTitle: user?.jobTitle || "",
      location: user?.location || "",
    },
    {
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      jobTitle: false,
      location: false,
    },
    {
      firstName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      lastName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
      phoneNumber: COMMON_FIELD_TYPES.PHONE,
      jobTitle: COMMON_FIELD_TYPES.TEXT_FIELD,
      location: COMMON_FIELD_TYPES.TEXT_FIELD,
    }
  );

  const [profilePicture, setProfilePicture] = useState(
    user?.profilePicture ?? null
  );

  // FUNCTION: Handle Profile Image Upload
  const handleProfileImageUpload = (file) => {
    setProfilePicture(file);
  };

  // NOTE: Helper function to get initial form data
  const getUserFormData = (user) => ({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    location: user?.location || "",
    jobTitle: user?.jobTitle || "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      jobTitle: true,
      location: true,
    });

    if (validateForm()) {
      
      let uploadedProfilePictureUrl = profilePicture || null;
      // first check if profile picture is null or not
      if (profilePicture === null) {
        // if profile picture is null it means user remove the profile picture or not uploaded
        // so we will set the profile picture to null
        setProfilePicture(null);
        uploadedProfilePictureUrl = null;
      } else {
        // Check if profile picture is a new file
        const isNewProfilePicture =
          (profilePicture && profilePicture instanceof File) ||
          (typeof profilePicture === "object" &&
            profilePicture.name &&
            profilePicture.type.startsWith("image/"));

        // Upload profile picture only if it's a new file
        if (isNewProfilePicture) {
          const uploadProfilePictureResult = await doUploadRefrencesToFirebase(
            [profilePicture],
            firebaseUplaodFolders.F_PROFILE_PICTURES
          );

          if (!uploadProfilePictureResult.success) {
            console.error("Upload Error:", uploadProfilePictureResult.error);
            showToast("error", "Failed to upload Profile Picture.");
            return;
          }

          uploadedProfilePictureUrl =
            uploadProfilePictureResult.uploadedUrls[0];
          setProfilePicture(uploadedProfilePictureUrl);
        }
      }

      const newFormData = {
        ...formData,
        profilePicture: uploadedProfilePictureUrl,
      };

      const result = await doUpdateProfile(newFormData);
      if (result?.success) {
        showToast("success", "Profile updated successfully");
        setIsEditing(false);
      } else {
        showToast("error", result.error?.message);
      }
    }
  };

  const handleCancel = () => {
    setFormData(getUserFormData(user));
    setProfilePicture(user?.profilePicture || null);
    setIsEditing(false);
  };

  return (
    <div className="my-12 px-[20px] md:px-[40px] lg:px-[100px]">
      <h1 className="font-bold font-nonito_sans text-3xl mb-10 sm:text-4xl lg:text-level-1 leading-tight sm:leading-[1.2] lg:leading-[47.5px] text-primary text-center tracking-[0.001em]">
        {isEditing ? "Edit Profile" : "My Profile"}
      </h1>

      <div className="p-5 sm:p-4 lg:p-9 border bg-white border-[#CBD5E0] rounded-[20px] lg:mb-10">
        <div className="pb-10">
          <div className="flex flex-col items-center lg:items-start sm:items-center md:items-start">
            <div className="mb-[10px] lg:mb-[30px] md:mb-[20px] sm:mb-[10px] ">
              <ProfileImageUploader
                label="Profile Image"
                isRequired={false}
                avatarContainerStyle="w-[119px]"
                imageStyle="w-[119px] h-[115px]"
                labelStyle="font-inter font-medium text-content"
                isDisabled={!isEditing}
                loading={isUpdateProfile}
                image={profilePicture}
                onImageUpload={handleProfileImageUpload}
                selectedImage={profilePicture}
              />
            </div>
          </div>

          <div className="flex flex-col">
            <div className="grid grid-cols-1 gap-0 lg:gap-[17px] md:gap-[17px] sm:gap-[16px] sm:grid-cols-2">
              <div className="mb-[10px] lg:mb-[30px] md:mb-[20px] sm:mb-[10px]">
                <CustomInput
                  label="First Name"
                  isRequired={true}
                  placeholder="Enter Your First Name"
                  // inputStyle="bg-[#E0F2F1] bg-opacity-30"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  error={touched.firstName && errors.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  onBlur={() => handleBlur("firstName", formData.firstName)}
                  className="font-medium font-inter text-content text-[14px] leading-[22px] w-full rounded-[8px] border border-[#B9C1CA] px-[16px] py-[13px] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[14px] placeholder:leading-[22px]"
                  disabled={!isEditing}
                  isFloatingLabel={false}
                  allowFillColor={false}
                />
              </div>

              <div className="mb-[10px] lg:mb-[30px] md:mb-[20px] sm:mb-[10px]">
                <CustomInput
                  label="Last Name"
                  isRequired={true}
                  placeholder="Enter Your Last Name"
                  // inputStyle="bg-[#E0F2F1] bg-opacity-30"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  error={touched.lastName && errors.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  onBlur={() => handleBlur("lastName", formData.lastName)}
                  className="font-medium font-inter text-content text-[14px] leading-[22px] w-full rounded-[8px] border border-[#B9C1CA] px-[16px] py-[13px] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[14px] placeholder:leading-[22px]"
                  disabled={!isEditing}
                  allowFillColor={false}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-0 lg:gap-[17px] md:gap-[17px] sm:gap-[16px] sm:grid-cols-2">
              <div className="mb-[10px] lg:mb-[30px] md:mb-[20px] sm:mb-[10px]">
                <CustomInput
                  label="Email Address"
                  isRequired={true}
                  placeholder="Enter Your Email Address"
                  // inputStyle="bg-[#E0F2F1] bg-opacity-30"
                  type="email"
                  name="email"
                  value={formData.email}
                  error={touched.email && errors.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onBlur={() => handleBlur("email", formData.email)}
                  className="font-medium font-inter text-content text-[14px] leading-[22px] w-full rounded-[8px] border border-[#B9C1CA] px-[16px] py-[13px] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[14px] placeholder:leading-[22px]"
                  disabled={true}
                  allowFillColor={false}
                />
              </div>

              <div className="mb-[10px] lg:mb-[30px] md:mb-[20px] sm:mb-[10px]">
                <CustomInput
                  label="Phone Number"
                  isRequired={true}
                  placeholder="Enter Your Phone Number"
                  // inputStyle="bg-[#E0F2F1] bg-opacity-30"
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  error={touched.phoneNumber && errors.phoneNumber}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  onBlur={() => handleBlur("phoneNumber", formData.phoneNumber)}
                  className="font-medium font-inter text-content text-[14px] leading-[22px] w-full rounded-[8px] border border-[#B9C1CA] px-[16px] py-[13px] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[14px] placeholder:leading-[22px]"
                  disabled={!isEditing}
                  isFloatingLabel={false}
                  allowFillColor={false}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-0 lg:gap-[17px] md:gap-[17px] sm:gap-[16px] sm:grid-cols-2">
              <div className="mb-[10px] lg:mb-[30px] md:mb-[20px] sm:mb-[10px]">
                <CustomInput
                  label="Job Title"
                  placeholder="Enter Your Job Title"
                  inputStyle=""
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  error={touched.jobTitle && errors.jobTitle}
                  onChange={(e) =>
                    handleInputChange("jobTitle", e.target.value)
                  }
                  onBlur={() => handleBlur("jobTitle", formData.jobTitle)}
                  className="placeholder:text-opacity-50 font-medium font-inter text-content text-[14px] leading-[22px]"
                  disabled={!isEditing}
                  allowFillColor={false}
                />
              </div>

              <div className="mb-[10px] lg:mb-[30px] md:mb-[20px] sm:mb-[10px]">
                <CustomInput
                  label="Location"
                  placeholder="Enter Your Location"
                  inputStyle=""
                  type="text"
                  name="location"
                  value={formData.location}
                  error={touched.location && errors.location}
                  onChange={(e) =>
                    handleInputChange("location", e.target.value)
                  }
                  onBlur={() => handleBlur("location", formData.location)}
                  className="placeholder:text-opacity-50 font-medium font-inter text-content text-[14px] leading-[22px]"
                  disabled={!isEditing}
                  allowFillColor={false}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-end gap-4 pt-12 border-t border-medium-gray">
          <Button
            buttonText={!isEditing ? "Edit Profile" : "Save Changes"}
            bgColor="bg-primary"
            textColor="text-white"
            width=""
            height=""
            icon={!isEditing ? <RiPencilFill size={16} /> : null}
            className="w-[159px] h-[46px] justify-center font-inter font-bold text-sm flex items-center "
            loaderColor="white"
            onClick={isEditing ? handleSubmit : handleEditing}
            loading={isUploadRefrencesToFirebaseLoading || isUpdateProfile}
            disabled={isUploadRefrencesToFirebaseLoading || isUpdateProfile}
          />
          <Button
            buttonText={!isEditing ? "Cancel" : "Back"}
            bgColor="ng-none"
            textColor="text-primary"
            width=""
            height=""
            className="px-6"
            onClick={handleCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
